import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { isElementReassigned } from '@src/hooks/useSpeakerOverrides';
import { adjustDOMClassesToTopicHighlight } from '@src/utility/topicHighlightDom';
import {
  clipHighlightStyles,
  OVERLAY_MATCH_ACTIVE_CLASS,
  overlayHighlightStyles,
  quoteHighlightStyles,
  savedSegmentHighlightStyles,
} from '@src/utility/transcriptQuoteUtils';
import { searchHighlightStyles } from '@src/utility/transcriptSearchUtils';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { checkMultipleWords, resolveBlockElementAtPoint } from './utils';

import type { SpeakerClickInfo } from './TranscriptionText';
import type { TranscriptDiffElement, TranscriptElementBlock } from './utils';
import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

const HIGHLIGHT_CURSOR_YELLOW = '#dfb615';

declare global {
  type CaretPosition = {
    offsetNode: Node;
    offset: number;
  };
  interface Document {
    caretPositionFromPoint?(x: number, y: number): CaretPosition;
  }
}

type TranscriptDiffElementMeta = {
  block: TranscriptElementBlock;
  element: TranscriptDiffElement;
  elementIndex: number;
  elementCharOffset: number;
};

type Props = {
  cursor: { from: number; to: number };
  blocks: TranscriptElementBlock[];
  onSpeakerClick?: (info: SpeakerClickInfo) => void;
  onWordClick?: (index: number) => void;
  /**
   * Whether the redline view is currently visible. The diff is kept mounted
   * with display:none toggled at the parent (Transcript.tsx), so transitions
   * false → true mark "user just opened redline" — the cue to scroll the
   * cursor into a comfortable read position.
   */
  isVisible?: boolean;
  /**
   * When set, only blocks whose underlying paragraph_id is in this set render.
   * Surrounding speaker headers / timestamps / paragraph breaks for filtered
   * blocks are dropped along with them. Undefined = render all (default).
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * When set, blocks whose underlying paragraph_id is in this set are
   * visually underlined (does not filter the render).
   */
  highlightedParagraphIds?: ReadonlySet<string>;
  /**
   * Per-paragraph underline colors keyed by paragraph_id. Falls back to the
   * default blue when missing.
   */
  paragraphHighlightColors?: ReadonlyMap<string, string>;
  /**
   * Paragraph_id of the currently-active topic match — its blocks render
   * with a thicker underline than the regular highlight.
   */
  activeParagraphId?: string;
  /**
   * Overlay (PLAT-10045) source-index ranges to underline. The redline view
   * collapses a run of same-state words into one block span, so a per-word
   * CSS class (the normal view's imperative pass) can't underline a sub-range
   * of a run. Instead we underline at RENDER TIME here: each block segments
   * its run at these range boundaries and wraps just the in-range slice. The
   * parent skips the imperative overlay pass while the original-language
   * redline is visible (see Transcript.tsx).
   */
  overlayRanges?: HighlightRange[];
};

type RenderItem =
  | {
      type: 'header';
      speakerLabel: string;
      startTime?: number;
      isDeleted: boolean;
      key: string;
      originalSpeakerLabel?: string;
      sectionIndices: number[];
    }
  | { type: 'timestamp'; startTime: number; isDeleted: boolean; key: string }
  | { type: 'paragraph-break'; key: string }
  | { type: 'block'; block: TranscriptElementBlock; index: number };

export const TranscriptionDiff = observer((props: Props) => {
  const {
    blocks,
    cursor,
    onSpeakerClick,
    onWordClick,
    visibleParagraphIds,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
    overlayRanges,
  } = props;
  // `isVisible` accepted for compat with Transcript.tsx (HEAD merge); dev's
  // overlay-driven approach doesn't gate on it directly.
  void props.isVisible;

  // Persistent overlay DOM nodes, mutated directly (no React state = no re-render on cursor/hover).
  const cursorOverlayRef = useRef<HTMLSpanElement>(null);
  const hoverOverlayRef = useRef<HTMLSpanElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Scope topic-highlight DOM queries to this instance's DiffWrapper —
  // multiple Transcript instances can coexist (see CLAUDE.md
  // "Duplicate Transcript Instances").
  const diffWrapperRef = useRef<HTMLDivElement>(null);

  const elementMetaByIndex = useMemo(() => {
    const metaMap = new Map<number, TranscriptDiffElementMeta>();
    blocks.forEach(block => {
      let elementCharOffset = 0;
      block.elements.forEach((element, index) => {
        const globalIndex = block.indexOffset + index;
        metaMap.set(globalIndex, {
          block,
          element,
          elementIndex: index,
          elementCharOffset,
        });
        elementCharOffset += element.value?.length || 0;
      });
    });
    return metaMap;
  }, [blocks]);

  const hideCursorOverlay = useCallback(() => {
    const el = cursorOverlayRef.current;
    if (el) el.style.visibility = 'hidden';
  }, []);

  const hideHoverOverlay = useCallback(() => {
    const el = hoverOverlayRef.current;
    if (el) el.style.display = 'none';
  }, []);

  // Walks the block's span text nodes to locate the element at `elementCharOffset`,
  // returns the target text node + adjusted offset, or null if not found.
  const locateCursorTarget = useCallback(
    (cursorFrom: number) => {
      let activeMeta = elementMetaByIndex.get(cursorFrom);
      let searchIndex = cursorFrom;
      while (
        activeMeta &&
        (!activeMeta.element.value ||
          activeMeta.element.state === 'removed' ||
          activeMeta.element.state === 'cut')
      ) {
        searchIndex++;
        activeMeta = elementMetaByIndex.get(searchIndex);
      }
      if (!activeMeta) return null;
      const { element, block, elementCharOffset } = activeMeta;
      if (
        !element.value ||
        element.state === 'removed' ||
        element.state === 'cut'
      ) {
        return null;
      }

      const blockIndexForQuery = block.originalIndexOffset ?? block.indexOffset;
      const spans = document.querySelectorAll<HTMLElement>(
        `[data-block-index="${blockIndexForQuery}"]`,
      );
      let targetSpan: HTMLElement | null = null;
      let adjustedCharOffset = elementCharOffset + (block.charOffset ?? 0);
      for (const span of spans) {
        const spanText = span.textContent || '';
        const spanLength = spanText.length;
        if (adjustedCharOffset < spanLength) {
          targetSpan = span;
          break;
        }
        adjustedCharOffset -= spanLength;
      }
      if (!targetSpan) return null;

      return { element, targetSpan, adjustedCharOffset };
    },
    [elementMetaByIndex],
  );

  // Updates the persistent cursor overlay (rect, text, visibility). No setState.
  // Always shown when the cursor points at a visible non-whitespace element,
  // regardless of play state — so a click while paused immediately highlights
  // the clicked word.
  const updateCursorOverlay = useCallback(() => {
    const overlay = cursorOverlayRef.current;
    if (!overlay) return;

    const canRenderOverlay = window.getSelection()?.type !== 'Range';
    if (!canRenderOverlay) {
      overlay.style.display = 'none';
      return;
    }

    const target = locateCursorTarget(cursor.from);
    if (!target) {
      overlay.style.display = 'none';
      return;
    }
    const { element, targetSpan, adjustedCharOffset } = target;
    if (element.value === ' ') {
      overlay.style.display = 'none';
      return;
    }

    const rect = getRect(targetSpan, element, adjustedCharOffset);

    overlay.textContent = element.value || '';
    overlay.dataset.state = element.state || '';
    overlay.style.top = `${rect.top}px`;
    overlay.style.left = `${rect.left}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
    overlay.style.display = 'inline-flex';

    const container = document.getElementById(
      'transcript-scrollable-container',
    );
    if (container) {
      const containerRect = container.getBoundingClientRect();
      // Store the scroll-relative offset so Transcript auto-scroll can
      // compute an accurate scrollTop without reading the fixed overlay's
      // viewport position (which goes stale after any scroll).
      overlay.dataset.scrollOffset = String(
        container.scrollTop + (rect.top - containerRect.top),
      );
      overlay.dataset.scrollHeight = String(rect.height);

      // Hide visually when off-screen but keep data attrs for scroll math.
      const threshold = rect.height * 0.5;
      const isVisible =
        rect.bottom >= containerRect.top + threshold &&
        rect.top <= containerRect.bottom - threshold;
      overlay.style.visibility = isVisible ? 'visible' : 'hidden';
    }
  }, [cursor.from, locateCursorTarget]);

  // Scroll: hide overlays while scrolling, re-run cursor placement after scroll settles.
  // Ref-latest pattern so the listener attaches once, not per cursor tick.
  const updateCursorOverlayRef = useRef(updateCursorOverlay);
  updateCursorOverlayRef.current = updateCursorOverlay;
  useEffect(() => {
    const container = document.getElementById(
      'transcript-scrollable-container',
    );
    if (!container) return;
    const handleScroll = () => {
      hideHoverOverlay();
      hideCursorOverlay();
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        updateCursorOverlayRef.current();
      }, 250);
    };
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [hideHoverOverlay, hideCursorOverlay]);

  // Cursor-driven overlay updates. Runs when cursor moves or when blocks change
  // (which causes updateCursorOverlay to get a new identity via locateCursorTarget).
  useEffect(() => {
    updateCursorOverlay();
  }, [cursor.from, cursor.to, updateCursorOverlay]);

  // Hover overlay: compute rect from caret position, mutate ref. No setState.
  const showHoverOverlay = useCallback(
    (element: TranscriptDiffElement, rect: DOMRect) => {
      const overlay = hoverOverlayRef.current;
      if (!overlay) return;
      if (window.getSelection()?.type === 'Range') {
        overlay.style.display = 'none';
        return;
      }
      if (!element.value || element.value === ' ') {
        overlay.style.display = 'none';
        return;
      }

      const hidden =
        element.state === 'replaced' || checkMultipleWords(element);
      overlay.textContent = element.value || '';
      overlay.style.top = `${rect.top}px`;
      overlay.style.left = `${rect.left}px`;
      overlay.style.width = hidden ? '0' : `${rect.width}px`;
      overlay.style.height = hidden ? '0' : `${rect.height}px`;
      overlay.style.display = 'inline-flex';
    },
    [],
  );

  const handleBlockMouseMove = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>, block: TranscriptElementBlock) => {
      const { elements, state } = block;
      const firstElement = elements[0];
      if (
        firstElement &&
        (state === 'replaced' || checkMultipleWords(firstElement))
      ) {
        const rect = e.currentTarget.getBoundingClientRect();
        showHoverOverlay(firstElement, rect);
        return;
      }

      const match = resolveBlockElementAtPoint(
        e.clientX,
        e.clientY,
        blocks,
        block,
      );
      if (match) {
        const rect = getRect(
          e.currentTarget,
          match.element,
          match.elementCharIndex,
        );
        showHoverOverlay(match.element, rect);
      }
    },
    [showHoverOverlay, blocks],
  );

  const handleBlockMouseLeave = useCallback(() => {
    hideHoverOverlay();
  }, [hideHoverOverlay]);

  const handleBlockClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>, block: TranscriptElementBlock) => {
      // stopPropagation so the Main-level onClick doesn't re-fire with the
      // block's indexOffset (which would seek to the first word of the block).
      e.stopPropagation();
      if (!onWordClick) return;

      const { elements, state } = block;
      const firstElement = elements[0];
      if (
        firstElement &&
        (state === 'replaced' || checkMultipleWords(firstElement))
      ) {
        onWordClick(block.indexOffset);
        return;
      }

      const match = resolveBlockElementAtPoint(
        e.clientX,
        e.clientY,
        blocks,
        block,
      );
      onWordClick(
        match
          ? match.block.indexOffset + match.elementIndex
          : block.indexOffset,
      );
    },
    [onWordClick, blocks],
  );

  // Topic highlight / active-match state applied via DOM class + CSS-var
  // mutation. Mirrors the cursor/hover overlay approach: prev/next
  // navigation and topic selection must NOT bust DiffContent's memo.
  // Deps include `blocks` and `visibleParagraphIds` so we re-apply after
  // a render that adds/removes block DOM nodes.
  useEffect(() => {
    adjustDOMClassesToTopicHighlight(
      diffWrapperRef.current,
      highlightedParagraphIds,
      activeParagraphId,
      paragraphHighlightColors,
    );
  }, [
    highlightedParagraphIds,
    activeParagraphId,
    paragraphHighlightColors,
    visibleParagraphIds,
    blocks,
  ]);

  return (
    <>
      <DiffWrapper ref={diffWrapperRef}>
        <DiffContent
          blocks={blocks}
          onSpeakerClick={onSpeakerClick}
          onBlockMouseMove={handleBlockMouseMove}
          onBlockMouseLeave={handleBlockMouseLeave}
          onBlockClick={handleBlockClick}
          visibleParagraphIds={visibleParagraphIds}
          overlayRanges={overlayRanges}
        />
      </DiffWrapper>

      <TranscriptSpanHoverOverlay ref={hoverOverlayRef} />
      <TranscriptSpanCursorOverlay
        ref={cursorOverlayRef}
        data-transcript-cursor-overlay=""
      />
    </>
  );
});

type DiffContentProps = {
  blocks: TranscriptElementBlock[];
  onSpeakerClick?: (info: SpeakerClickInfo) => void;
  onBlockMouseMove: (
    e: React.MouseEvent<HTMLSpanElement>,
    block: TranscriptElementBlock,
  ) => void;
  onBlockMouseLeave: () => void;
  onBlockClick: (
    e: React.MouseEvent<HTMLSpanElement>,
    block: TranscriptElementBlock,
  ) => void;
  visibleParagraphIds?: ReadonlySet<string>;
  overlayRanges?: HighlightRange[];
};

// Rendered ONCE per `blocks` identity change. Cursor/hover/topic-highlight
// never re-render this tree — they mutate the DOM directly.
const DiffContentInner: React.FC<DiffContentProps> = ({
  blocks,
  onSpeakerClick,
  onBlockMouseMove,
  onBlockMouseLeave,
  onBlockClick,
  visibleParagraphIds,
  overlayRanges,
}) => {
  const { getSpeakerName, getSpeakerColor } = useSpeakerLabels();

  const renderItems = useMemo(() => {
    const items: RenderItem[] = [];
    let lastSpeaker: string | undefined;
    let lastParagraphId: string | undefined;

    const blockIsInScope = (block: TranscriptElementBlock): boolean => {
      if (!visibleParagraphIds) return true;
      const pid = block.elements.find(el => el.type === 'text')?.paragraph_id;
      return pid !== undefined && visibleParagraphIds.has(pid);
    };

    const blockHasVisibleContent = (block: TranscriptElementBlock): boolean => {
      if (block.state === 'removed' || block.state === 'cut') return false;
      return block.elements.some(
        el =>
          el.type === 'text' &&
          el.value?.trim() &&
          el.state !== 'removed' &&
          el.state !== 'cut',
      );
    };

    const speakerSectionHasVisibleContent = (
      startIndex: number,
      speakerLabel: string,
    ): boolean => {
      for (let i = startIndex; i < blocks.length; i++) {
        const block = blocks[i];
        const blockSpeaker = block.elements.find(
          el => el.type === 'text',
        )?.speaker_label;
        if (blockSpeaker !== speakerLabel && blockSpeaker !== undefined) break;
        if (!blockIsInScope(block)) continue;
        if (blockHasVisibleContent(block)) return true;
      }
      return false;
    };

    blocks.forEach((block, index) => {
      if (!blockIsInScope(block)) return;

      const anyFirstTextElement = block.elements.find(el => el.type === 'text');
      const firstVisibleTextElement = block.elements.find(
        el =>
          el.type === 'text' && el.state !== 'removed' && el.state !== 'cut',
      );

      const hasVisibleContent = blockHasVisibleContent(block);
      const hasDeletedContent =
        block.state === 'removed' ||
        block.state === 'cut' ||
        block.elements.some(
          el =>
            (el.state === 'removed' || el.state === 'cut') && el.value?.trim(),
        );

      if (!hasVisibleContent && !hasDeletedContent) {
        items.push({ type: 'block', block, index });
        return;
      }

      const speakerLabel = anyFirstTextElement?.speaker_label;
      const startTime =
        firstVisibleTextElement?.ts ?? anyFirstTextElement?.ts ?? undefined;
      const paragraphId = anyFirstTextElement?.paragraph_id;

      const isNewSpeaker =
        speakerLabel !== lastSpeaker && speakerLabel !== undefined;
      const isNewParagraph =
        paragraphId !== lastParagraphId && paragraphId !== undefined;

      if (isNewSpeaker && speakerLabel) {
        const sectionHasVisible = speakerSectionHasVisibleContent(
          index,
          speakerLabel,
        );
        const reassignedFrom =
          anyFirstTextElement && isElementReassigned(anyFirstTextElement)
            ? anyFirstTextElement.original_speaker_label
            : undefined;
        const sectionIndices: number[] = [];
        for (let si = index; si < blocks.length; si++) {
          const blk = blocks[si];
          const blkSpeaker = blk.elements.find(
            el => el.type === 'text',
          )?.speaker_label;
          if (
            si > index &&
            blkSpeaker !== speakerLabel &&
            blkSpeaker !== undefined
          )
            break;
          blk.elements.forEach((_, ei) =>
            sectionIndices.push(blk.indexOffset + ei),
          );
        }
        items.push({
          type: 'header',
          speakerLabel,
          startTime: hasVisibleContent ? startTime : undefined,
          isDeleted: !sectionHasVisible,
          key: `header-${index}`,
          originalSpeakerLabel: reassignedFrom,
          sectionIndices,
        });
        lastSpeaker = speakerLabel;
        lastParagraphId = paragraphId;
      } else if (
        isNewParagraph &&
        (hasVisibleContent || hasDeletedContent) &&
        startTime !== undefined
      ) {
        items.push({
          type: 'timestamp',
          startTime,
          isDeleted: !hasVisibleContent,
          key: `timestamp-${index}`,
        });
        lastParagraphId = paragraphId;
      } else if (isNewParagraph) {
        items.push({ type: 'paragraph-break', key: `break-${index}` });
        lastParagraphId = paragraphId;
      }

      items.push({ type: 'block', block, index });
    });

    return items;
  }, [blocks, visibleParagraphIds]);

  return (
    <>
      {renderItems.map(item => {
        if (item.type === 'header') {
          return (
            <SpeakerHeader key={item.key}>
              <SpeakerLabelRow>
                <SpeakerLabel
                  $color={getSpeakerColor(item.speakerLabel)}
                  $isDeleted={item.isDeleted}
                  $clickable={!!onSpeakerClick}
                  onClick={
                    onSpeakerClick
                      ? e => {
                          e.stopPropagation();
                          onSpeakerClick({
                            speakerLabel: item.speakerLabel,
                            sectionIndices: item.sectionIndices,
                            targetElement: e.currentTarget as HTMLElement,
                          });
                        }
                      : undefined
                  }
                >
                  {getSpeakerName(item.speakerLabel)}
                </SpeakerLabel>
                {item.originalSpeakerLabel && (
                  <ReassignedTag>
                    was {getSpeakerName(item.originalSpeakerLabel)}
                  </ReassignedTag>
                )}
              </SpeakerLabelRow>
              {item.startTime !== undefined && (
                <SpeakerTime>{formatTime(item.startTime)}</SpeakerTime>
              )}
            </SpeakerHeader>
          );
        }
        if (item.type === 'timestamp') {
          return (
            <TimestampRow key={item.key} $isDeleted={item.isDeleted}>
              {formatTime(item.startTime)}
            </TimestampRow>
          );
        }
        if (item.type === 'paragraph-break') {
          return <ParagraphSpacer key={item.key} />;
        }
        const blockPid = item.block.elements.find(
          el => el.type === 'text',
        )?.paragraph_id;
        return (
          <TranscriptDiffBlock
            key={`block-${item.index}`}
            block={item.block}
            paragraphId={blockPid}
            onMouseMove={onBlockMouseMove}
            onMouseLeave={onBlockMouseLeave}
            onClick={onBlockClick}
            overlayRanges={overlayRanges}
          />
        );
      })}
    </>
  );
};

const DiffContent = React.memo(
  DiffContentInner,
  (prev, next) =>
    prev.blocks === next.blocks &&
    prev.visibleParagraphIds === next.visibleParagraphIds &&
    prev.overlayRanges === next.overlayRanges,
);

type TranscriptDiffBlockProps = {
  block: TranscriptElementBlock;
  paragraphId?: string;
  onMouseMove: (
    e: React.MouseEvent<HTMLSpanElement>,
    block: TranscriptElementBlock,
  ) => void;
  onMouseLeave: () => void;
  onClick: (
    e: React.MouseEvent<HTMLSpanElement>,
    block: TranscriptElementBlock,
  ) => void;
  overlayRanges?: HighlightRange[];
};

const TranscriptDiffBlock: React.FC<TranscriptDiffBlockProps> = ({
  block,
  paragraphId,
  onMouseMove,
  onMouseLeave,
  onClick,
  overlayRanges,
}) => {
  const { state, elements } = block;

  // Build the block's children once per block.
  // Uniform-state blocks collapse to a single text node (the common path).
  // Mixed-state blocks (muted fillers inside a monologue, etc.) coalesce
  // consecutive same-state elements into runs — one text node per matching-state
  // run, one <span> per differing-state run. Minimizes DOM children while keeping
  // per-element coloring correct. Hit-testing in resolveElementAtPoint still works
  // because its sibling walk sums textContent.length regardless of run size.
  const children = useMemo<React.ReactNode>(() => {
    if (state === 'replaced') return elements[0].value || '';

    // PLAT-10519 — overlay underline at RENDER TIME. This block can hold a
    // whole run of words while an overlay range covers only a sub-span of it,
    // so a CSS class on the block span (the normal view's imperative pass)
    // would underline the entire run. Instead we segment the run at the range
    // boundaries and wrap just the in-range slice. `block.indexOffset + i` is
    // the element's source index — the same space `findOverlayRanges`
    // produces (and `data-index` carries). A removed word bracketed by the
    // range is its own block but tests in-range here too, so the underline
    // stays continuous through it.
    // The overlay range id covering element `i` (null = not underlined). We
    // carry the id, not just a boolean, so the underline span can be tagged
    // `data-overlay-id` and brightened per-overlay on hover/selection (see
    // `brightenOverlayUnderlinesById`) without touching the strikethrough.
    const overlayIdAt = (i: number): string | null => {
      if (!overlayRanges?.length) return null;
      const gi = block.indexOffset + i;
      const r = overlayRanges.find(r => gi >= r.start && gi <= r.end);
      return r ? r.id : null;
    };
    const anyUnderline =
      !!overlayRanges?.length &&
      elements.some((_, i) => overlayIdAt(i) !== null);

    // Common path: no overlay touches this block and it is uniform-state.
    if (!anyUnderline && elements.every(el => el.state === state)) {
      return elements.map(el => el.value || '').join('');
    }

    // Coalesce consecutive elements that share both run-state AND underline
    // membership into one node — a plain string when neither color nor
    // underline applies, else a <span>. Underlined slices use OverlayUnderline
    // (its own styled span, never the imperative `overlay-match` class, so the
    // clear pass can't strip it).
    const out: React.ReactNode[] = [];
    let i = 0;
    while (i < elements.length) {
      const runState = elements[i].state;
      const overlayId = overlayIdAt(i);
      const segStart = i;
      let segText = '';
      while (
        i < elements.length &&
        elements[i].state === runState &&
        overlayIdAt(i) === overlayId
      ) {
        segText += elements[i].value || '';
        i++;
      }
      const color =
        runState === state ? undefined : getColorFromState(runState);
      if (overlayId) {
        out.push(
          <OverlayUnderline
            key={`seg-${segStart}`}
            data-overlay-id={overlayId}
            style={color ? { color } : undefined}
          >
            {segText}
          </OverlayUnderline>,
        );
      } else if (color) {
        out.push(
          <span key={`seg-${segStart}`} style={{ color }}>
            {segText}
          </span>,
        );
      } else {
        out.push(segText);
      }
    }
    return out;
  }, [state, elements, overlayRanges, block.indexOffset]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => onMouseMove(e, block),
    [onMouseMove, block],
  );
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => onClick(e, block),
    [onClick, block],
  );

  return (
    <>
      {state === 'replaced' && elements[0].originalValue && (
        <ReplacedElement>{elements[0].originalValue}</ReplacedElement>
      )}
      <TranscriptSpan
        data-block-index={block.originalIndexOffset ?? block.indexOffset}
        data-char-offset={block.charOffset ?? 0}
        data-index={block.indexOffset}
        data-index-end={block.indexOffset + block.elements.length}
        data-paragraph-id={paragraphId}
        data-state={state}
        state={state}
        onMouseMove={handleMouseMove}
        onMouseLeave={onMouseLeave}
        onClick={handleClick}
        hover={elements.length === 1 && checkMultipleWords(elements[0])}
      >
        {children}
      </TranscriptSpan>
    </>
  );
};

const findTextNodeAtOffset = (
  container: ChildNode,
  charOffset: number,
): { textNode: Text; offset: number } | null => {
  if (container.nodeType === Node.TEXT_NODE) {
    return { textNode: container as Text, offset: charOffset };
  }
  let remaining = charOffset;
  for (const child of container.childNodes) {
    const len = child.textContent?.length || 0;
    if (remaining < len || child === container.lastChild) {
      return findTextNodeAtOffset(child, remaining);
    }
    remaining -= len;
  }
  return null;
};

const getRect = (
  containerNode: ChildNode,
  element: TranscriptDiffElement,
  elementCharIndex = 0,
) => {
  const result = findTextNodeAtOffset(containerNode, elementCharIndex);
  if (!result) return new DOMRect();

  const { textNode, offset } = result;
  const range = document.createRange();
  const textLength = textNode.textContent?.length || 0;
  const startIndex = Math.min(offset, textLength);
  const endIndex = Math.min(offset + (element.value?.length || 0), textLength);
  range.setStart(textNode, startIndex);
  range.setEnd(textNode, endIndex);
  return range.getBoundingClientRect();
};

const getColorFromState = (state: TranscriptDiffElement['state']) => {
  switch (state) {
    case 'added':
      return '#7bb975';
    case 'removed':
      return '#cd5c5c';
    case 'replaced':
      return 'var(--text-secondary)';
    case 'muted':
      return '#F69B12';
    case 'cut':
      return '#7bb97599';
    default:
      return 'var(--text-secondary)';
  }
};

const TranscriptSpanHoverOverlay = styled.span`
  display: none;
  align-items: center;
  position: fixed;
  pointer-events: none;
  user-select: none;
  background: var(--background);
  color: #90ee90;
`;

const TranscriptSpanCursorOverlay = styled.span`
  display: none;
  align-items: center;
  position: fixed;
  pointer-events: none;
  user-select: none;
  background: ${HIGHLIGHT_CURSOR_YELLOW};
  color: var(--background);
  font-weight: var(--font-weight-regular);

  &[data-state='muted'] {
    color: var(--background);
  }
`;

const DiffWrapper = styled.div`
  padding-left: 2px;
`;

// PLAT-10519 — render-time overlay underline for the redline view. Mirrors
// `overlayHighlightStyles` (navy `--content-overlays`, continuous through
// descenders) but as a declarative styled span the imperative apply/clear
// pass (which targets the `overlay-match` class) never touches.
const OverlayUnderline = styled.span`
  text-decoration: underline;
  text-decoration-color: var(--content-overlays);
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  text-decoration-skip-ink: none;
  /* Brighten only THIS span's underline when its overlay is hovered/selected
     (class toggled imperatively by brightenOverlayUnderlinesById). Scoped to
     the underline's own text-decoration-color, so a removed word's red
     strikethrough — owned by the parent block span — is never recolored. */
  &.${OVERLAY_MATCH_ACTIVE_CLASS} {
    text-decoration-color: var(--content-overlays-hover);
  }
`;

const ParagraphSpacer = styled.div`
  display: block;
  margin-top: 10px;
`;

const ReplacedElement = styled.span`
  color: #b59b14;
  text-decoration: line-through;
`;

const TranscriptSpan = styled.span<{
  state: TranscriptDiffElement['state'];
  hover?: boolean;
}>`
  color: ${({ state }) => getColorFromState(state)};
  cursor: pointer;
  user-select: text;
  ${({ state }) =>
    state &&
    {
      added: css``,
      cut: css`
        text-decoration: line-through #7bb97580;
      `,
      removed: css`
        text-decoration: line-through #cd5c5c80;
      `,
      replaced: css`
        &:hover {
          color: #90ee90;
        }
      `,
      muted: css``,
    }[state]}
  ${({ hover }) =>
    hover &&
    css`
      &:hover {
        color: #90ee90;
      }
    `}

  /*
   * Topic-highlight classes applied imperatively by
   * adjustDOMClassesToTopicHighlight (bypasses React re-renders).
   * The per-paragraph color is set via the --topic-highlight-color
   * inline style on the same span. The :not() guards keep the
   * existing strike-through on cut/removed blocks — otherwise the
   * underline shorthand would clobber it.
   */
  &.topic-highlighted:not([data-state='cut']):not([data-state='removed']),
  &.topic-active:not([data-state='cut']):not([data-state='removed']) {
    text-decoration: underline;
    text-decoration-color: var(--topic-highlight-color, var(--badge-blue-text));
    text-underline-offset: 3px;
  }
  &.topic-highlighted:not([data-state='cut']):not([data-state='removed']) {
    text-decoration-thickness: 2px;
  }
  &.topic-active:not([data-state='cut']):not([data-state='removed']) {
    text-decoration-thickness: 4px;
  }

  ${searchHighlightStyles}
  ${quoteHighlightStyles}
  ${clipHighlightStyles}
  ${savedSegmentHighlightStyles}
  ${overlayHighlightStyles}
`;

const SpeakerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  margin-top: 10px;
  user-select: none;
  pointer-events: none;

  &:first-child {
    margin-top: 0;
  }
`;

const SpeakerLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpeakerLabel = styled.span<{
  $color?: string;
  $isDeleted?: boolean;
  $clickable?: boolean;
}>`
  color: ${props => props.$color || 'var(--accent)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  ${props => props.$isDeleted && `text-decoration: line-through;`}
  ${props =>
    props.$clickable &&
    `
    pointer-events: auto;
    cursor: pointer;
    padding: 2px 8px;
    margin-left: -8px;
    border-radius: 4px;
    transition: background 0.15s ease;
    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }
  `}
`;

const ReassignedTag = styled.span`
  font-size: 10px;
  color: #c084fc;
  background: rgba(107, 33, 168, 0.3);
  border-radius: 3px;
  padding: 1px 6px;
  white-space: nowrap;
`;

const SpeakerTime = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
`;

const TimestampRow = styled.div<{ $isDeleted?: boolean }>`
  text-align: right;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 1;
  margin-top: 10px;
  margin-bottom: 4px;
  user-select: none;
  pointer-events: none;
  ${props => props.$isDeleted && `text-decoration: line-through;`}
`;
